import { useQuery } from '@tanstack/react-query'
import { requestCenterApi } from '../api/request-center.api'

/**
 * Consumer hook for the `/requests/stats/my` endpoint, scoped to the
 * fields the navigation sidebar cares about.
 *
 * Uses the same TanStack Query key as `useDashboard()` (`['dashboard',
 * 'stats', 'my']`) so the two consumers share cache: the sidebar
 * mounts on every authenticated page; when the dashboard also mounts
 * it reads from this same warm entry without re-fetching. Key kept
 * inline rather than promoted to `requestCenterKeys.stats.my()` because
 * the partner key on `useDashboard` is still inline — promoting one
 * side would split the cache bucket.
 *
 * The `staleTime` matches the dashboard (60s); the sidebar gets
 * stale-while-revalidate semantics for free.
 *
 * Downstream (Sidebar, filterNav) treats the value as a best-effort
 * hint — security is never enforced on it; backend endpoints gate
 * access independently.
 */
export interface StatsMy {
	hadEverPendingApproval: boolean
}

export function useStatsMy(): StatsMy {
	const { data } = useQuery({
		queryKey: ['dashboard', 'stats', 'my'],
		queryFn: () => requestCenterApi.myStats(),
		staleTime: 60_000,
		// Don't disrupt the sidebar with retry storms on a flaky endpoint —
		// the bare hint we expose is best-effort. The hook either knows
		// the user has been an approver (true) or doesn't (false).
		retry: false,
	})
	return { hadEverPendingApproval: data?.hadEverPendingApproval ?? false }
}
